import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  queryChanged,
  SearchElement,
  selectSearchQuery,
  selectSearchResults,
} from './searchSlice';
import { colors } from '../utils/colors';
import { titleize } from '../utils/stringUtils';
import { useDebouncedCallback } from '../helpers/hooks';
import { usePriceComponents } from '../hooks/usePriceComponents';
import { selectedItemsSlice } from '../selectedItems/selectedItemsSlice';
import { Point } from '@/types';
import { MapIcon } from '@iconicicons/react';
import { useFitMap, usePanMap } from '../plotter/usePanMap';

const SEARCH_BOX_PADDING_EM = '.5em';

const getElementColor = (type: SearchElement['type']) => {
  if (type === 'point') return colors.matteBlue;
  if (type === 'interconnect') return colors.elements.interconnectColor;
  if (type === 'priceComponent') return colors.elements.maxMarketColor;
  return colors.matteBlue;
};

export const MapSearch: React.FC = () => {
  usePriceComponents();
  const [isSearching, setIsSearching] = useState(false);
  const [navbarHeight, setNavbarHeight] = useState(0);
  const panMap = usePanMap();
  const fitMap = useFitMap();
  const dispatch = useDispatch();
  const search = useSelector(selectSearchQuery);
  const searchResults = useSelector(selectSearchResults);
  const searchAreaRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    const navbarElement = document.getElementById('app-navbar');
    if (!navbarElement) return;
    setNavbarHeight(navbarElement.clientHeight);
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.target === navbarElement) {
          setNavbarHeight(entry.borderBoxSize[0].blockSize);
        }
      }
    });
    resizeObserver.observe(navbarElement);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const endSearch = useCallback(() => {
    setIsSearching(false);
  }, []);

  const onBlur = useCallback(
    (evt: MouseEvent) => {
      if (!searchAreaRef.current?.contains(evt.target as Node)) {
        endSearch();
      }
    },
    [endSearch],
  );

  const startSearch = useCallback(() => {
    setIsSearching(true);
  }, []);

  const setSearch = useDebouncedCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(queryChanged({ searchQuery: evt.target.value }));
    },
    500,
    [dispatch, queryChanged],
  );

  useEffect(() => {
    window.addEventListener('click', onBlur);
    return () => {
      window.removeEventListener('click', onBlur);
    };
  }, [onBlur]);

  const selectItem = useCallback(
    (element: SearchElement) => {
      dispatch(
        selectedItemsSlice.actions.itemClicked({
          id: element.id,
          itemType: element.type,
        }),
      );
      if (element.location && element.location.length > 0) {
        element.location.length === 1
          ? panMap(element.location[0], 14)
          : fitMap(element.location);
      }
      if (inputRef.current) {
        inputRef.current.value = element.displayName;
      }
      endSearch();
    },
    [dispatch, endSearch, queryChanged, inputRef],
  );

  return (
    <div
      className="w-100 d-flex justify-content-center"
      style={{ position: 'relative' }}
    >
      <div
        ref={searchAreaRef}
        className="d-flex justify-content-center flex-column"
        style={{
          position: 'fixed',
          top: `calc(${navbarHeight}px + ${SEARCH_BOX_PADDING_EM})`,
          zIndex: 3,
          width: '32%',
          minWidth: 300,
        }}
      >
        <input
          className="form-control w-100"
          style={{
            width: '32%',
            minWidth: 300,
            borderRadius: 16,
            overflow: 'hidden',
          }}
          placeholder="Search"
          onChange={setSearch}
          onFocus={startSearch}
          ref={inputRef}
        />
        <div
          className="bg-light rounded w-100 mt-2 border"
          style={{
            maxHeight: '60vh',
            overflowY: 'auto',
            display: isSearching && search.length ? 'block' : 'none',
          }}
        >
          {searchResults.length === 0 && (
            <div className="p-2 mx-1">
              <p className="text-muted text-center">No results found</p>
            </div>
          )}
          {searchResults.map((element, index) => {
            if (element.type === 'point') {
              return (
                <MapSearchPointResult
                  index={index}
                  key={`${element.key}`}
                  element={element}
                  onSelectItem={selectItem}
                />
              );
            } else {
              return (
                <div
                  key={`${element.key}`}
                  className="p-2 mx-1"
                  role="button"
                  style={{
                    borderTop:
                      index > 0 ? '1px solid var(--bs-border-color)' : 'none',
                  }}
                  onClick={() => selectItem(element)}
                >
                  <p className="fw-bold">
                    <span
                      style={{
                        color: getElementColor(element.type),
                      }}
                    >
                      {titleize(element.type)}
                    </span>{' '}
                    #{element.id}
                  </p>
                  <p>{element.displayName}</p>
                </div>
              );
            }
          })}
        </div>
      </div>
    </div>
  );
};

type MapSearchPointResultProps = {
  element: SearchElement;
  index: number;
  onSelectItem: (element: SearchElement) => void;
};

const MapSearchPointResult: React.FC<MapSearchPointResultProps> = ({
  element,
  index,
  onSelectItem,
}) => {
  const selectItem = useCallback(() => {
    onSelectItem(element);
  }, [onSelectItem, element]);

  const point = element.instance as Point;

  return (
    <div
      key={`${element.key}`}
      className="p-2 mx-1"
      role="button"
      style={{
        borderTop: index > 0 ? '1px solid var(--bs-border-color)' : 'none',
      }}
      onClick={selectItem}
    >
      <div className="d-flex justify-content-between">
        <p className="fw-bold">
          <span
            style={{
              color: colors.primaryBlue,
            }}
          >
            {titleize(element.type)}
          </span>{' '}
          #{element.id}
        </p>
        {!point.node && (
          <div className="text-warning">
            <MapIcon className="me-1" /> NOT MAPPED
          </div>
        )}
      </div>
      <h6 className="my-0">
        {point.name} ({point.loc_id})
      </h6>
      <div className="clearfix">
        <small className="float-start">
          {point.tsp_name}: {point.receipt_zone_name}
        </small>
        <small className="float-end">{point.loc_cnty}</small>
      </div>
      {point.up_dn_name && point.up_dn_name.length > 0 && (
        <small className="mt-3">Connects to {point.up_dn_name}</small>
      )}
      <div className="mt-3">
        {point.capacity_components?.length > 0 && (
          <>
            <div>
              <small>
                <strong>Capacities</strong>
              </small>
            </div>
            {point.capacity_components.map(
              (cc) =>
                Boolean(cc?.id) && (
                  <div key={cc.id.toString()} className="text-muted">
                    <small>{cc.label ?? cc.description}</small>
                  </div>
                ),
            )}
          </>
        )}
      </div>
      <div className="mt-3">
        {point.price_components?.length > 0 && (
          <>
            <div>
              <small>
                <strong>Markets</strong>
              </small>
            </div>
            {point.price_components.map(
              (pc) =>
                Boolean(pc?.id) && (
                  <div key={pc.id.toString()} className="text-muted">
                    <small>{pc.description}</small>
                  </div>
                ),
            )}
          </>
        )}
      </div>
    </div>
  );
};
